import { useState } from "react";

import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";

interface AdvancedSubViewProps {
  updateTransientPreview: (updates: any) => void;
}

export function AdvancedSubView({
  updateTransientPreview,
}: AdvancedSubViewProps) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);


  const handleExecuteReset = () => {
    updateTransientPreview({
      layoutOverrides: {},
      backgroundStyle: "solid",
      animationLevel: "subtle",
    });
    setShowResetConfirm(false);
    toast.success("Design overrides reset to theme defaults.");
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="border-b border-border pb-3">
        <h3 className="text-base font-bold text-foreground">Advanced Customization & Reset</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Restore custom style overrides back to clean template defaults.
        </p>
      </div>

      {/* ── 1. Reset All Style Overrides ── */}
      <div className="space-y-3 border-t border-border pt-6">
        <div className="flex items-center gap-2">
          <RotateCcw className="h-4 w-4 text-muted-foreground" />
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Reset Style Overrides
          </Label>
        </div>

        <div className="p-4 sm:p-5 rounded-2xl border border-border bg-card shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1 max-w-lg">
            <div className="text-sm font-semibold text-foreground">Restore Clean Theme Defaults</div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Resets all custom margins, hero alignments, and 2.5D bevels back to the active theme's defaults. Your portfolio sections, publications, and content remain completely untouched.
            </p>
          </div>

          {!showResetConfirm ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowResetConfirm(true)}
              className="shrink-0 text-xs font-semibold text-destructive hover:bg-destructive/10 hover:border-destructive/40 cursor-pointer"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset Overrides
            </Button>
          ) : (
            <div className="flex items-center gap-2 shrink-0 animate-in fade-in duration-150">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleExecuteReset}
                className="text-xs font-semibold cursor-pointer"
              >
                Confirm Reset
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowResetConfirm(false)}
                className="text-xs cursor-pointer"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
